<script setup>
import { computed } from "vue";
import Spinner from "@/components/Spinner/Spinner.vue";

const props = defineProps({
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["extra-small", "small", "medium", "large"].includes(value)
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  isActive: {
    type: Boolean,
    default: false
  },
  iconOnly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["toggle"]);

const buttonClasses = computed(() => [
  "button-fixed-primary",
  `button-fixed-primary--${props.size}`,
  {
    "button-fixed-primary--icon-only": props.iconOnly,
    "button-fixed-primary--active": props.isActive,
    "button-fixed-primary--inactive": !props.isActive,
    "button-fixed-primary--active--loading": props.loading && props.isActive,
    "button-fixed-primary--inactive--loading": props.loading && !props.isActive
  }
]);
</script>

<template>
  <button
    :disabled="props.disabled"
    :class="buttonClasses"
    :aria-pressed="props.isActive ? 'true' : 'false'"
    type="button"
    @click="emit('toggle')"
  >
    <Spinner
      v-if="loading"
      class="spinner"
    />
    <slot
      v-else
      name="icon-left"
    />
    <slot />
    <slot name="icon-right" />
  </button>
</template>

<style scoped lang="scss">

.button-fixed-primary {
  // Сброс дефолтных стилей.
  border: none;
  outline: none;
  background: none;
  margin: 0;
  font: inherit;

  // Стили кнопки.
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  padding: 0 14px;

  border-radius: 50px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;

  cursor: pointer;
  user-select: none;

  // Если кнопка содержит только иконку - убираем отступы, задаем ширину равной высоте.
  &--icon-only {
    padding: 0;
    min-width: var(--button-height);
    width: var(--button-height);
  }

  // Активное состояние
  &--active {
    background: var(--fix-button-primary-active-default);
    color: var(--fix-button-primary-active-text);

    &:hover:not(:disabled):not(&--loading) {
      background: var(--fix-button-primary-active-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--fix-button-primary-active-text);
      outline-offset: 2px;
    }

    &:disabled {
      pointer-events: none;
      background: var(--fix-button-primary-active-disabled);
    }

    &--loading {
      background: var(--fix-button-primary-active-loading);

      ::v-deep(.spinner) {
        border-color: var(--fix-button-primary-active-text);
        border-bottom-color: transparent;
      }
    }
  }

  // Неактивное состояние
  &--inactive {
    background: var(--fix-button-primary-inactive-default);
    color: var(--fix-button-primary-inactive-text);

    &:hover:not(:disabled):not(&--loading) {
      background: var(--fix-button-primary-inactive-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--fix-button-primary-inactive-text);
      outline-offset: 2px;
    }

    &:disabled {
      pointer-events: none;
      background: var(--fix-button-primary-inactive-disabled);
      color: var(--text-text-disabled);
    }

    &--loading {
      background: var(--fix-button-primary-inactive-loading);

      ::v-deep(.spinner) {
        border-color: var(--fix-button-primary-inactive-text);
        border-bottom-color: transparent;
      }
    }
  }

  // Размеры
  &--extra-small {
    --button-height: 24px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--small {
    --button-height: 30px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--medium {
    --button-height: 36px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--large {
    --button-height: 42px;
    height: var(--button-height);
    min-height: var(--button-height);
  }
}

</style>